import { 
    Button, Col, Container, Form, FormControl, Row, 
    Card, InputGroup, Spinner, Alert, Badge, OverlayTrigger, Tooltip
} from "react-bootstrap";
import DataTable from 'react-data-table-component';
import { useEffect, useState } from "react";
import { changeUserDetail, getAllUsers, getAllWebhookConnections } from "../service/UserService";
import * as PropTypes from "prop-types";
import { Link } from "react-router-dom";
import { 
    FaUser, FaUserPlus, FaSearch, FaSync, 
    FaEdit, FaToggleOn, FaToggleOff, FaFilter,
    FaExternalLinkAlt, FaLink
} from "react-icons/fa";

function UpdateButtonMenu(props) {
    return (
        <Link to={"/manage-user/user-details/" + props.row.username}>
            <Button size="sm" variant="outline-primary">
                <FaEdit className="me-1" /> Edit
            </Button>
        </Link>
    );
}

function UpdateBlockStatus(props) {
    const [isUpdating, setIsUpdating] = useState(false);

    function updateBlockStatus(event) {
        setIsUpdating(true);
        let requestBody = {
            blocked: event.target.checked
        };
        
        changeUserDetail(props.row.username, requestBody)
            .then(response => {
                if (response.status === 200) {
                    props.refreshUsers();
                }
            })
            .catch(error => console.log('error', error))
            .finally(() => setIsUpdating(false));
    }

    return (
        <div className="d-flex align-items-center">
            {isUpdating ? (
                <Spinner animation="border" size="sm" className="me-2" />
            ) : (
                props.row.blocked ? 
                <FaToggleOn className="text-danger me-2" size="1.2em" /> : 
                <FaToggleOff className="text-success me-2" size="1.2em" />
            )}
            <Form.Check
                inline 
                type="checkbox" 
                id={`block-${props.row.username}`}
                checked={props.row.blocked}
                onChange={updateBlockStatus} 
                disabled={props.row.role !== 'AGENT' || isUpdating}
            />
        </div>
    );
}

UpdateButtonMenu.propTypes = { size: PropTypes.string };

const sortBlockedStatus = (rowA, rowB) => {
    const a = rowA.blocked;
    const b = rowB.blocked;

    if (a > b) {
        return 1;
    }

    if (b > a) {
        return -1;
    }

    return 0;
};

const customStyles = {
    rows: {
        style: {
            minHeight: '40px', // slightly taller rows
        },
    },
    headCells: {
        style: {
            fontWeight: 'bold',
            fontSize: '0.9rem',
            backgroundColor: '#f8f9fa',
            paddingTop: '12px',
            paddingBottom: '12px',
        },
    },
    cells: {
        style: {
            paddingTop: '8px',
            paddingBottom: '8px',
        },
    },
};

export default function ManageUser() {
    const [tableData, setTableData] = useState([]);
    const [filteredData, setFilteredData] = useState([]);
    const [roleFilterSelection, setRoleFilterSelection] = useState("ALL");
    const [searchText, setSearchText] = useState("");
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState(null);
    const [webhookConnections, setWebhookConnections] = useState([]);
    const columns = [
        {
            name: 'ID',
            selector: row => row.cos_customer_id,
            sortable: true,
            width: '70px',
        },
        {
            name: 'Username',
            selector: row => row.username,
            sortable: true,
            cell: row => <span className="fw-semibold">{row.username}</span>,
        },
        {
            name: 'Blocked',
            selector: row => <UpdateBlockStatus size="small" row={row} refreshUsers={refreshUsers}/>,
            sortable: true,
            sortFunction: sortBlockedStatus,
            width: '100px',
        },
        {
            name: 'Name',
            selector: row => row.cos_customer_name,
            sortable: true,
        },
        {
            name: 'Agent Name',
            selector: row => row.agent_name,
            sortable: true,
        },
        {
            name: 'Role',
            selector: row => row.role,
            sortable: true,
            cell: row => {
                let variant = 'secondary';
                if (row.role === 'ADMIN') variant = 'danger';
                if (row.role === 'AGENT') variant = 'primary';
                if (row.role === 'WORKER') variant = 'success';
                if (row.role === 'SUPERVISOR') variant = 'warning';
                
                return <Badge bg={variant}>{row.role}</Badge>;
            },
            width: '120px',
        },
        {
            name: 'Phone',
            selector: row => row.phone,
            sortable: true,
        },
        {
            name: 'Email',
            selector: row => row.email,
            sortable: true,
        },
        {
            name: 'Region',
            selector: row => row.region,
            sortable: true,
        },
        {
            name: 'Integration',
            selector: row => row.cos_customer_id,
            sortable: true,
            cell: row => {
                const hasGuestyIntegration = webhookConnections.some(
                    webhook => webhook.cos_customer_id === row.cos_customer_id
                );
                
                return (
                    <div className="d-flex align-items-center">
                        <Link to={`/manage-user/guesty-integration/${row.cos_customer_id}`}>
                            <OverlayTrigger
                                placement="top"
                                overlay={<Tooltip>{hasGuestyIntegration ? "Manage Guesty Integration" : "Setup Guesty Integration"}</Tooltip>}
                            >
                                <span>
                                    {hasGuestyIntegration ? (
                                        <img 
                                            src="/guesty_log.jpg" 
                                            alt="Guesty" 
                                            style={{ width: '24px', height: '24px', objectFit: 'contain' }} 
                                        />
                                    ) : (
                                        <img 
                                            src="/guesty_log.jpg" 
                                            alt="No Guesty Integration" 
                                            style={{ 
                                                width: '24px', 
                                                height: '24px', 
                                                objectFit: 'contain',
                                                opacity: 0.3,
                                                filter: 'grayscale(100%)'
                                            }} 
                                        />
                                    )}
                                </span>
                            </OverlayTrigger>
                        </Link>
                    </div>
                );
            },
            width: '120px',
        },
        {
            cell: row => <UpdateButtonMenu size="small" row={row}/>,
            allowoverflow: true,
            button: "true",
            width: '80px',
        }
    ];

    function refreshUsers() {
        setIsLoading(true);
        setError(null);
        
        // Refresh webhook connections when refreshing users
        fetchWebhookConnections();
        
        getAllUsers()
            .then(response => {
                if (response.status === 200) {
                    response.json().then(result => filterTables(result, roleFilterSelection, searchText));
                } else {
                    response.text().then(result => {
                        console.log(result);
                        setError("Failed to load users: " + result);
                    });
                }
            })
            .catch(error => {
                console.log('error', error);
                setError("An error occurred while fetching users");
            })
            .finally(() => {
                setIsLoading(false);
            });
    }

    // Fetch webhook connections
    function fetchWebhookConnections() {
        getAllWebhookConnections()
            .then(response => {
                if (response.status === 200) {
                    response.json().then(result => {
                        if (result.success && result.webhooks) {
                            setWebhookConnections(result.webhooks);
                        }
                    });
                }
            })
            .catch(error => {
                console.log('Error fetching webhook connections:', error);
            });
    }

    // Call Refresh button automatically when component mounts
    useEffect(() => {
        refreshUsers(); // This will also fetch webhook connections
    }, []);

    function filterTables(rawdata, role, searchText) {
        let tempData = rawdata;
        
        if (role !== 'ALL') {
            tempData = rawdata.filter(
                item => item.role && item.role.toLowerCase().includes(role.toLowerCase()),
            );
        }
        
        // Filter by cos_customer_name, username, agent_name, phone, email
        if (searchText && searchText.length > 0) {
            tempData = tempData.filter(
                item => (item.username && item.username.toLowerCase().includes(searchText.toLowerCase())
                    || (item.agent_name && item.agent_name.toLowerCase().includes(searchText.toLowerCase()))
                    || (item.cos_customer_name && item.cos_customer_name.toLowerCase().includes(searchText.toLowerCase()))
                    || (item.phone && item.phone.toLowerCase().includes(searchText.toLowerCase()))
                    || (item.email && item.email.toLowerCase().includes(searchText.toLowerCase()))
                )
            );
        }
        
        setTableData(rawdata);
        setFilteredData(tempData);
    }
    function toggleRoleFilter(event) {
        setRoleFilterSelection(event.target.id);
        filterTables(tableData, event.target.id, searchText);
    }

    function filterSearchText(value) {
        setSearchText(value);
        filterTables(tableData, roleFilterSelection, value);
    }

    return (
        <Container fluid className="py-4">
            <Card className="shadow-sm">
                <Card.Header as="h5" className="bg-primary text-white d-flex justify-content-between align-items-center">
                    <div>
                        <FaUser className="me-2" /> Manage Users
                    </div>
                    <Button 
                        variant="light" 
                        size="sm" 
                        onClick={refreshUsers}
                        disabled={isLoading}
                    >
                        <FaSync className={isLoading ? "me-2 fa-spin" : "me-2"} /> Refresh
                    </Button>
                </Card.Header>
                <Card.Body>
                    {error && (
                        <Alert variant="danger" className="mb-4">
                            {error}
                        </Alert>
                    )}
                    
                    <Row className="mb-4 align-items-center">
                        <Col md={6}>
                            <Card className="border-light bg-light">
                                <Card.Body className="py-2">
                                    <div className="d-flex align-items-center">
                                        <FaFilter className="me-2 text-primary" />
                                        <span className="fw-bold me-2">Filter by Role:</span>
                                        <Form className="d-flex flex-wrap">
                                            <div key={`inline-radio`} className="d-flex flex-wrap">
                                                <Form.Check
                                                    inline
                                                    label="ADMIN"
                                                    name="group1"
                                                    type="radio"
                                                    checked={roleFilterSelection === "ADMIN"}
                                                    onChange={toggleRoleFilter}
                                                    id="ADMIN"
                                                    className="me-2"
                                                />
                                                <Form.Check
                                                    inline
                                                    label="AGENT"
                                                    name="group1"
                                                    type="radio"
                                                    checked={roleFilterSelection === "AGENT"}
                                                    onChange={toggleRoleFilter}
                                                    id="AGENT"
                                                    className="me-2"
                                                />
                                                <Form.Check
                                                    inline
                                                    name="group1"
                                                    label="WORKER"
                                                    type="radio"
                                                    checked={roleFilterSelection === "WORKER"}
                                                    onChange={toggleRoleFilter}
                                                    id="WORKER"
                                                    className="me-2"
                                                />
                                                <Form.Check
                                                    inline
                                                    name="group1"
                                                    label="SUPERVISOR"
                                                    type="radio"
                                                    checked={roleFilterSelection === "SUPERVISOR"}
                                                    onChange={toggleRoleFilter}
                                                    id="SUPERVISOR"
                                                    className="me-2"
                                                />
                                                <Form.Check
                                                    inline
                                                    name="group1"
                                                    label="All"
                                                    type="radio"
                                                    checked={roleFilterSelection === "ALL"}
                                                    onChange={toggleRoleFilter}
                                                    id="ALL"
                                                    className="me-2"
                                                />
                                            </div>
                                        </Form>
                                    </div>
                                </Card.Body>
                            </Card>
                        </Col>
                        <Col md={3} className="mt-3 mt-md-0">
                            <Link to="/manage-user/add-user" className="text-decoration-none">
                                <Button variant="success" className="w-100">
                                    <FaUserPlus className="me-2" /> Add New User
                                </Button>
                            </Link>
                        </Col>
                        <Col md={3} className="mt-3 mt-md-0">
                            <InputGroup>
                                <InputGroup.Text>
                                    <FaSearch />
                                </InputGroup.Text>
                                <FormControl
                                    value={searchText}
                                    onChange={(e) => filterSearchText(e.target.value)}
                                    type="search"
                                    placeholder="Search users..."
                                    aria-label="Search"
                                />
                            </InputGroup>
                        </Col>
                    </Row>
                    
                    {isLoading && !filteredData.length ? (
                        <div className="text-center py-5">
                            <Spinner animation="border" variant="primary" />
                            <p className="mt-3">Loading users...</p>
                        </div>
                    ) : (
                        <DataTable
                            columns={columns}
                            data={filteredData}
                            customStyles={customStyles}
                            pagination
                            highlightOnHover
                            responsive
                            striped
                            progressPending={isLoading && !filteredData.length}
                            progressComponent={<Spinner animation="border" variant="primary" />}
                            noDataComponent={
                                <div className="p-4 text-center">
                                    No users found matching your criteria
                                </div>
                            }
                        />
                    )}
                </Card.Body>
            </Card>
        </Container>
    );
}
